import React, { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Accordion, AccordionSummary } from '@mui/material'
import { PAYMENT_METHODS, PAYMENT_STATUS } from 'settings/constants/payment'
import { INVOICE_GENERATION_STATUS } from 'settings/constants/billing'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { isPaymentsV15FeatureAvailable } from 'data/launch-darkly/featureSelectors'
import InvoiceAccordionSummarySkeleton from 'components/customer-details/content/invoices/InvoiceAccordionSummarySkeleton'
import InvoiceAccordionSummary from 'components/customer-details/content/invoices/InvoiceAccordionSummary'
import InvoiceActivityFeed from '../../../billing/common/InvoiceActivityFeed'
import { INVOICE_ACTIVITY_TABS_ID } from './settings'

const { APPROVED } = PAYMENT_STATUS
const { ACCOUNT_CREDIT } = PAYMENT_METHODS
const { PAYMENT_TAB_ID } = INVOICE_ACTIVITY_TABS_ID

const InvoiceAccordion = ({
  isViewOnly = false,
  isLoading = false,
  selectedTab,
  invoice = {},
  emailEvents = [],
  onClick,
  onActionClick,
  pollingExhausted = false,
  isPollingActive = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [selectedSubTab, setSelectedSubTab] = useState(PAYMENT_TAB_ID)
  const { id: invoiceId, payments = [], paymentSummary = [], accountCredits = [] } = invoice
  const flags = useFlags()
  const isPaymentsV15Enabled = isPaymentsV15FeatureAvailable({ flags })

  const allPayments = useMemo(() => {
    if (isPaymentsV15Enabled) {
      return paymentSummary.map(paymentSummaryItem => {
        const { paymentMethod, appliedAmount } = paymentSummaryItem
        const isAccountCredit = paymentMethod === ACCOUNT_CREDIT
        if (isAccountCredit) {
          return {
            ...paymentSummaryItem,
            transactionStatus: APPROVED,
            transactionAmount: appliedAmount,
          }
        }
        return paymentSummaryItem
      })
    }
    return [
      ...payments,
      ...accountCredits.map(accountCredit => ({
        isAccountCredit: true,
        paymentMethod: ACCOUNT_CREDIT,
        transactionAmountCents: accountCredit?.amountCents,
        transactionPostedDate: accountCredit?.createdAt,
        transactionStatus: APPROVED,
      })),
    ]
  }, [payments, accountCredits, isPaymentsV15Enabled, paymentSummary])

  const { latestBatchStatus } = invoice
  const isGenerating =
    isPollingActive &&
    !pollingExhausted &&
    (latestBatchStatus === INVOICE_GENERATION_STATUS.Started || latestBatchStatus === INVOICE_GENERATION_STATUS.Regeneration_Started)

  const handleTabChange = (event, newValue) => setSelectedSubTab(newValue)
  const onExpandClick = () => setIsExpanded(!isExpanded)

  useEffect(() => {
    if (selectedTab) {
      setIsExpanded(false)
      setSelectedSubTab(PAYMENT_TAB_ID)
    }
  }, [selectedTab, isLoading])

  return (
    <Accordion
      disableGutters
      sx={{ boxShadow: 'none', '&.Mui-expanded:before': { opacity: '1' }, '&.Mui-expanded': { margin: 'auto' } }}
      expanded={isExpanded}
    >
      <AccordionSummary onClick={isGenerating ? undefined : onClick}>
        {isLoading && <InvoiceAccordionSummarySkeleton />}
        {!isLoading && (
          <InvoiceAccordionSummary
            invoice={invoice}
            isViewOnly={isViewOnly}
            onActionClick={onActionClick}
            onExpandClick={onExpandClick}
            isExpanded={isExpanded}
            allPayments={allPayments}
            emailEvents={emailEvents}
            isGenerating={isGenerating}
          />
        )}
      </AccordionSummary>
      <InvoiceActivityFeed
        selectedTab={selectedSubTab}
        onSelectedTabChange={handleTabChange}
        payments={isPaymentsV15Enabled ? paymentSummary : payments}
        accountCredits={accountCredits}
        emailEvents={emailEvents}
        invoiceId={invoiceId}
      />
    </Accordion>
  )
}

InvoiceAccordion.propTypes = {
  isViewOnly: PropTypes.bool,
  isLoading: PropTypes.bool,
  selectedTab: PropTypes.string.isRequired,
  invoice: PropTypes.object,
  emailEvents: PropTypes.array,
  onClick: PropTypes.func.isRequired,
  onActionClick: PropTypes.func.isRequired,
  pollingExhausted: PropTypes.bool,
  isPollingActive: PropTypes.bool,
}

export default InvoiceAccordion
